'use client';

import { useState } from 'react';

import Link from 'next/link';

import {
  AlertTriangle,
  Bell,
  Calendar,
  CheckCircle,
  Clock,
  Network,
  Package,
  RotateCcw,
  Ship,
  TrendingUp,
} from 'lucide-react';

import { formatQuantity } from '@kit/shared/quantity';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@kit/ui/accordion';
import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';

import appConfig from '~/config/app.config';
import pathsConfig from '~/config/paths.config';
import { useIsHydrated } from '~/lib/use-is-hydrated';

import { RelatedTools } from '../_components/related-tools';
import {
  calculateLeadTime,
  getLeadTimeInputError,
  parseNumericInput,
  parseOptionalInput,
} from './_lib/calculate-lead-time';

const baseUrl = appConfig.url;
const siteName = appConfig.name;
const calculatorUrl = `${baseUrl}/tools/lead-time-calculator`;
const toolsUrl = `${baseUrl}/tools`;

type FormData = {
  currentStock: string;
  customsDays: string;
  dailyDemand: string;
  productionDays: string;
  safetyBufferDays: string;
  transitDays: string;
};

const DEFAULT_FORM_DATA: FormData = {
  currentStock: '1500',
  customsDays: '5',
  dailyDemand: '20',
  productionDays: '30',
  safetyBufferDays: '14',
  transitDays: '21',
};
const HYDRATION_REFERENCE_DATE = new Date('2000-01-01T12:00:00Z');

const heroHighlights = [
  'No signup required',
  'Safety stock built in',
  'Order deadline included',
] as const;

const planningSteps = [
  {
    title: '1. Supplier and production time',
    description:
      'Start with how long your supplier needs to produce and prepare the goods before they can ship. This is the first term in any lead time calculation.',
    icon: Package,
  },
  {
    title: '2. Transit and customs time',
    description:
      'Add ocean or air transit, customs clearance, and the handoff into your warehouse. These are the days most reorder plans forget.',
    icon: Ship,
  },
  {
    title: '3. Safety buffer discipline',
    description:
      'The buffer absorbs late shipments and demand spikes. It sits on top of lead time rather than inside it, so you can see what each one costs you.',
    icon: Bell,
  },
] as const;

const whyItMatters = [
  {
    title: 'Avoid preventable stockouts',
    description:
      'Reorder points only work when lead time and buffer assumptions are grounded in reality.',
  },
  {
    title: 'Order with better timing',
    description:
      'Knowing the latest safe order date makes replenishment decisions easier across sourcing, freight, and inventory teams.',
  },
  {
    title: 'Set realistic safety stock',
    description:
      'A structured lead-time model helps you carry the right buffer instead of guessing based on recent pain.',
  },
] as const;

const faqItems = [
  {
    value: 'free',
    question: 'Is this lead time calculator free?',
    answer:
      'Yes. This free lead time calculator runs entirely in your browser with no signup, no usage limit, and no credit card. Nothing you enter is sent to a server, and results update as you type.',
  },
  {
    value: 'reorder-point',
    question: 'What is the reorder point formula?',
    answer:
      'Reorder point equals demand during lead time plus safety stock. This calculator uses daily demand multiplied by production, transit, and customs days, then adds the safety stock your buffer implies.',
  },
  {
    value: 'lead-time-formula',
    question: 'How do you calculate total lead time?',
    answer:
      'Add production time, transit time, and customs clearance. This page keeps the safety buffer out of that total on purpose: lead time is what your supply chain actually takes, and the buffer is a cushion you choose on top of it.',
  },
  {
    value: 'safety-stock',
    question: 'How much safety stock should I hold?',
    answer:
      'That depends on lead-time variability and demand variability. This calculator models safety stock as days of demand covered by your buffer, which is the simplest usable method. Many importers start with two to four weeks, then adjust based on supplier reliability.',
  },
  {
    value: 'lead-time-components',
    question: 'What should be included in import lead time?',
    answer:
      'Include production time, transit time, customs clearance, and any final delivery delay into your warehouse. If any one of those is consistently missed, your reorder point needs to reflect it.',
  },
  {
    value: 'deadline',
    question: 'How is the latest order date calculated?',
    answer:
      'The calculator estimates stockout timing from current stock and daily demand, then counts backward by lead time plus buffer. If that date has already passed, the page tells you how many days late you are rather than quietly showing today.',
  },
  {
    value: 'chart-calendar',
    question: 'Can I use this as a lead time chart or lead time calendar?',
    answer:
      'Yes. The output gives total lead time, reorder point, safety stock, and the latest order date, so teams can use it like a lead time chart or a calendar checkpoint before each replenishment cycle.',
  },
  {
    value: 'worked-example',
    question: 'What does the example on this page show?',
    answer:
      'The defaults model an ocean import: 30 days production, 21 days transit, 5 days customs (56-day lead time), 20 units of daily demand, 1,500 units on hand, and a 14-day buffer. Lead-time demand is 1,120 units, safety stock is 280, and the reorder point is 1,400. You have 75 days of cover, so the order-by date is about 5 days from today (75 − 56 − 14). Replace every field with one SKU.',
  },
] as const;

const statusStyles = {
  healthy: {
    panel: 'border-sage-fg/40 bg-sage-bg text-sage-fg',
    iconClass: 'text-sage-fg',
    label: 'Healthy stock',
  },
  warning: {
    panel: 'border-warn-fg/40 bg-warn-bg text-warn-fg',
    iconClass: 'text-warn-fg',
    label: 'Reorder now',
  },
  critical: {
    panel: 'border-destructive/40 bg-destructive/10 text-destructive',
    iconClass: 'text-destructive',
    label: 'Critical stock',
  },
} as const;

function StructuredData() {
  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Lead Time Calculator: When to Place the Next PO',
    alternateName: 'Reorder Point and Safety Stock Calculator',
    description:
      'Add production, transit, and customs days. Get lead time, reorder point, and the latest date the next PO can go out.',
    url: calculatorUrl,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Lead Time and Reorder Point Calculator',
    operatingSystem: 'Any',
    browserRequirements: 'Requires JavaScript',
    inLanguage: 'en',
    isAccessibleForFree: true,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    creator: { '@type': 'Organization', name: siteName, url: baseUrl },
    featureList: [
      'Calculate total import lead time',
      'Calculate reorder point',
      'Estimate safety stock from a buffer',
      'Identify the latest safe order date',
      'Flag overdue replenishment',
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: 'Free Tools', item: toolsUrl },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Lead Time Calculator',
        item: calculatorUrl,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webAppSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}

function formatIsoDate(isoDate: string) {
  const [year, month, day] = isoDate.split('-').map(Number);

  return new Date(year!, month! - 1, day!).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function LeadTimeCalculatorPage() {
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [clientReferenceDate] = useState(() => new Date());
  const isHydrated = useIsHydrated();
  const referenceDate = isHydrated
    ? clientReferenceDate
    : HYDRATION_REFERENCE_DATE;

  const startFreePath = pathsConfig.auth.startFree;
  const customsDays = parseNumericInput(formData.customsDays);
  const productionDays = parseNumericInput(formData.productionDays);
  const transitDays = parseNumericInput(formData.transitDays);
  const inputError = getLeadTimeInputError({
    customsDays,
    productionDays,
    transitDays,
  });
  const results = inputError
    ? null
    : calculateLeadTime(
        {
          currentStock: parseOptionalInput(formData.currentStock),
          customsDays,
          dailyDemand: parseOptionalInput(formData.dailyDemand),
          productionDays,
          safetyBufferDays: parseNumericInput(formData.safetyBufferDays),
          transitDays,
        },
        referenceDate,
      );
  const hasResults = results !== null;
  const currentStatus =
    hasResults && results.status ? statusStyles[results.status] : null;

  function updateField<K extends keyof FormData>(key: K, value: FormData[K]) {
    setFormData((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function resetExample() {
    setFormData(DEFAULT_FORM_DATA);
  }

  const reorderPointLabel = !hasResults
    ? '--'
    : results.reorderPoint === null
      ? 'Add daily demand'
      : formatQuantity(results.reorderPoint);

  const safetyStockLabel = !hasResults
    ? '--'
    : results.safetyStock === null
      ? 'Add daily demand'
      : `${formatQuantity(results.safetyStock)} units`;

  const daysUntilStockoutLabel = !hasResults
    ? '--'
    : results.daysUntilStockout === null
      ? 'Add stock and demand'
      : `${results.daysUntilStockout} days`;

  const orderByLabel = !hasResults
    ? '--'
    : !isHydrated
      ? 'Calculating date'
      : results.orderByDate === null
        ? 'Add stock and demand'
        : formatIsoDate(results.orderByDate);

  const orderByCaption =
    hasResults && results.orderByDays !== null
      ? results.orderByDays < 0
        ? `Overdue by ${Math.abs(results.orderByDays)} days`
        : results.orderByDays === 0
          ? 'That date is today'
          : `${results.orderByDays} days from now`
      : null;

  const coverageLabel = !hasResults
    ? '--'
    : results.coverageWeeksAtReorder === null
      ? 'Add daily demand'
      : `${results.coverageWeeksAtReorder} weeks`;

  return (
    <>
      <StructuredData />

      <div className="bg-background text-foreground min-h-screen">
        <section className="border-border bg-muted relative overflow-hidden border-b">
          <div className="absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(circle_at_top_right,_color-mix(in_srgb,var(--primary)_calc(0.12*100%),transparent),_transparent_42%)]" />
          <div className="bg-primary/10 absolute -top-28 right-0 h-72 w-72 rounded-full blur-3xl" />

          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="max-w-2xl">
                <div className="bg-primary/10 text-primary inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs font-bold tracking-[0.18em] uppercase">
                  <Clock className="h-4 w-4" />
                  Free inventory planning tool
                </div>

                <h1 className="font-heading mt-6 max-w-2xl text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                  <span className="text-primary">Lead Time Calculator</span>:
                  Reorder Point and Next PO Date
                </h1>

                <p className="text-muted-foreground mt-6 max-w-xl text-lg leading-8">
                  Free tool for Shopify, Amazon FBA, and importers. Add
                  production, transit, and customs days to get total lead time,
                  reorder point, safety stock, and the latest date the next PO
                  can go out.
                </p>

                <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
                  {heroHighlights.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <CheckCircle className="text-primary h-4 w-4" />
                      <span className="text-muted-foreground text-sm font-medium">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <p className="text-muted-foreground mt-8 text-sm leading-6">
                  The calculator opens with a realistic ocean-import example.
                  Replace the defaults with one SKU&apos;s actual numbers to see
                  when its reorder window closes.
                </p>
              </div>

              <div className="bg-foreground text-background rounded-[10px] p-7 shadow-[0_24px_70px_-30px_rgba(38,31,28,0.7)] sm:p-9">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <p className="text-background/70 text-xs font-bold tracking-[0.18em] uppercase">
                      Total replenishment lead time
                    </p>
                    <p className="mt-3 text-5xl font-extrabold tracking-tight tabular-nums">
                      {hasResults ? `${results.leadTimeDays} days` : '--'}
                    </p>
                  </div>
                  <div className="bg-background/10 rounded-2xl p-3">
                    <Clock className="h-7 w-7" />
                  </div>
                </div>

                <div className="border-background/20 mt-8 grid grid-cols-2 gap-4 border-t pt-7">
                  {[
                    ['Reorder point', reorderPointLabel],
                    ['Safety stock', safetyStockLabel],
                    ['Days of cover', daysUntilStockoutLabel],
                    ['Order by', orderByLabel],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="bg-background/10 rounded-xl p-4"
                    >
                      <p className="text-background/70 text-xs">{label}</p>
                      <p className="mt-1 text-lg font-bold tabular-nums">
                        {value}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-between gap-4 text-sm">
                  <span className="text-background/70">Planning horizon</span>
                  <span className="font-bold tabular-nums">
                    {hasResults ? `${results.planningHorizonDays} days` : '--'}
                  </span>
                </div>
              </div>

              <div className="border-border bg-card rounded-[10px] border p-6 shadow-[0_20px_50px_-28px_rgba(22,22,42,0.35)] sm:p-8 lg:col-span-2 lg:p-10">
                <div className="mb-6 flex items-center gap-3">
                  <div className="bg-primary/10 flex h-11 w-11 items-center justify-center rounded-xl">
                    <Clock className="text-primary h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-foreground text-2xl font-extrabold">
                      Calculate Lead Time and Reorder Point
                    </h2>
                    <p className="text-muted-foreground text-sm">
                      Results update instantly as you replace the example
                      values.
                    </p>
                  </div>
                  <div className="ml-auto hidden gap-2 sm:flex">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetExample}
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reset example
                    </Button>
                  </div>
                </div>

                <form
                  onSubmit={(e) => e.preventDefault()}
                  className="space-y-5"
                >
                  <div className="space-y-3">
                    <p className="text-muted-foreground text-xs font-bold tracking-[0.2em] uppercase">
                      Lead Time Inputs
                    </p>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label
                          htmlFor="productionDays"
                          className="text-muted-foreground text-sm font-bold"
                        >
                          Production Time (days)
                        </Label>
                        <Input
                          id="productionDays"
                          type="number"
                          min="0"
                          step="1"
                          value={formData.productionDays}
                          onChange={(e) =>
                            updateField('productionDays', e.target.value)
                          }
                          placeholder="30"
                          className="border-border bg-muted h-12 rounded-xl shadow-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="transitDays"
                          className="text-muted-foreground text-sm font-bold"
                        >
                          Transit Time (days)
                        </Label>
                        <Input
                          id="transitDays"
                          type="number"
                          min="0"
                          step="1"
                          value={formData.transitDays}
                          onChange={(e) =>
                            updateField('transitDays', e.target.value)
                          }
                          placeholder="21"
                          className="border-border bg-muted h-12 rounded-xl shadow-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="customsDays"
                          className="text-muted-foreground text-sm font-bold"
                        >
                          Customs Clearance (days)
                        </Label>
                        <Input
                          id="customsDays"
                          type="number"
                          min="0"
                          step="1"
                          value={formData.customsDays}
                          onChange={(e) =>
                            updateField('customsDays', e.target.value)
                          }
                          placeholder="5"
                          className="border-border bg-muted h-12 rounded-xl shadow-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="safetyBufferDays"
                          className="text-muted-foreground text-sm font-bold"
                        >
                          Safety Buffer (days)
                        </Label>
                        <Input
                          id="safetyBufferDays"
                          type="number"
                          min="0"
                          step="1"
                          value={formData.safetyBufferDays}
                          onChange={(e) =>
                            updateField('safetyBufferDays', e.target.value)
                          }
                          placeholder="14"
                          className="border-border bg-muted h-12 rounded-xl shadow-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-border space-y-3 border-t pt-4">
                    <p className="text-muted-foreground text-xs font-bold tracking-[0.2em] uppercase">
                      Demand and Inventory
                    </p>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label
                          htmlFor="dailyDemand"
                          className="text-muted-foreground text-sm font-bold"
                        >
                          Daily Demand (units, optional)
                        </Label>
                        <Input
                          id="dailyDemand"
                          type="number"
                          min="0"
                          step="0.1"
                          value={formData.dailyDemand}
                          onChange={(e) =>
                            updateField('dailyDemand', e.target.value)
                          }
                          placeholder="20"
                          className="border-border bg-muted h-12 rounded-xl shadow-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="currentStock"
                          className="text-muted-foreground text-sm font-bold"
                        >
                          Current Stock (units, optional)
                        </Label>
                        <Input
                          id="currentStock"
                          type="number"
                          min="0"
                          step="1"
                          value={formData.currentStock}
                          onChange={(e) =>
                            updateField('currentStock', e.target.value)
                          }
                          placeholder="1500"
                          className="border-border bg-muted h-12 rounded-xl shadow-none"
                        />
                      </div>
                    </div>
                  </div>

                  {inputError ? (
                    <div
                      aria-live="polite"
                      className="border-destructive/40 bg-destructive/10 text-destructive flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm"
                    >
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <p>{inputError}</p>
                    </div>
                  ) : null}

                  <p className="text-muted-foreground text-xs leading-5">
                    This is a planning model. Final reorder decisions should
                    still reflect supplier reliability, seasonality, and actual
                    demand swings.
                  </p>

                  <div className="flex gap-2 sm:hidden">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={resetExample}
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reset
                    </Button>
                  </div>
                </form>

                <div className="border-border mt-8 flex flex-col gap-5 border-t pt-8 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 text-primary hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:flex">
                      <Network className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-base font-bold">
                        A reorder date only helps if someone watches it.
                      </p>
                      <p className="text-muted-foreground mt-1 max-w-xl text-sm leading-6">
                        {siteName} keeps stock, supplier lead times, and
                        purchase orders together, then flags the SKUs whose
                        reorder windows are closing.
                      </p>
                    </div>
                  </div>
                  <Button
                    asChild
                    className="h-11 shrink-0 rounded-xl px-6 font-bold"
                  >
                    <Link href={startFreePath}>Start Your 14-Day Trial</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-border bg-card border-y py-12">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="border-primary/10 bg-primary/[0.06] rounded-[10px] border p-6 sm:p-8">
              <div className="space-y-2">
                <p className="text-muted-foreground text-sm font-bold tracking-[0.22em] uppercase">
                  Replenishment Results
                </p>
                <h2 className="font-heading text-foreground text-2xl font-bold">
                  {hasResults
                    ? 'Lead time and reorder analysis'
                    : 'Run a calculation to see the safe reorder threshold'}
                </h2>
              </div>

              <div className="mt-8 space-y-6">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-end">
                  <div>
                    <p className="text-muted-foreground text-xs font-bold tracking-[0.2em] uppercase">
                      Total Lead Time
                    </p>
                    <p className="text-primary text-3xl font-black sm:text-4xl">
                      {hasResults ? `${results.leadTimeDays} days` : '--'}
                    </p>
                    <p className="text-muted-foreground mt-1 text-xs leading-5">
                      Production + transit + customs
                    </p>
                  </div>

                  <div className="border-border border-l pl-0 sm:pl-6">
                    <p className="text-muted-foreground text-xs font-bold tracking-[0.2em] uppercase">
                      Reorder Point
                    </p>
                    <p className="text-foreground text-3xl font-black sm:text-4xl">
                      {reorderPointLabel}
                    </p>
                    <p className="text-muted-foreground mt-1 text-xs leading-5">
                      {hasResults
                        ? `Covers a ${results.planningHorizonDays}-day horizon with the buffer`
                        : 'Lead-time demand plus safety stock'}
                    </p>
                  </div>
                </div>

                {hasResults && results.statusMessage && currentStatus ? (
                  <div
                    className={`rounded-2xl border p-4 ${currentStatus.panel}`}
                  >
                    <div className="flex items-start gap-3">
                      {results.status === 'healthy' ? (
                        <CheckCircle
                          className={`mt-0.5 h-5 w-5 shrink-0 ${currentStatus.iconClass}`}
                        />
                      ) : (
                        <AlertTriangle
                          className={`mt-0.5 h-5 w-5 shrink-0 ${currentStatus.iconClass}`}
                        />
                      )}
                      <div>
                        <p className="text-sm font-bold tracking-[0.16em] uppercase">
                          {currentStatus.label}
                        </p>
                        <p className="mt-1 text-sm leading-6">
                          {results.statusMessage}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="border-border bg-card/70 rounded-2xl border p-4">
                    <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                      Safety Stock
                    </p>
                    <p className="text-foreground mt-2 text-xl font-bold">
                      {safetyStockLabel}
                    </p>
                  </div>

                  <div className="border-border bg-card/70 rounded-2xl border p-4">
                    <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                      Days Until Stockout
                    </p>
                    <p className="text-foreground mt-2 text-xl font-bold">
                      {daysUntilStockoutLabel}
                    </p>
                  </div>

                  <div className="border-border bg-card/70 rounded-2xl border p-4">
                    <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                      Latest Order Date
                    </p>
                    <p className="text-foreground mt-2 text-xl font-bold">
                      {orderByLabel}
                    </p>
                    {orderByCaption ? (
                      <p
                        className={`mt-1 text-xs font-semibold ${
                          hasResults &&
                          results.orderByDays !== null &&
                          results.orderByDays < 0
                            ? 'text-destructive'
                            : 'text-muted-foreground'
                        }`}
                      >
                        {orderByCaption}
                      </p>
                    ) : null}
                  </div>

                  <div className="border-border bg-card/70 rounded-2xl border p-4">
                    <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                      Coverage At Reorder
                    </p>
                    <p className="text-foreground mt-2 text-xl font-bold">
                      {coverageLabel}
                    </p>
                  </div>
                </div>

                {hasResults && results.warnings.length > 0 ? (
                  <ul className="border-warn-fg/40 bg-warn-bg text-warn-fg space-y-3 rounded-2xl border p-5 text-sm">
                    {results.warnings.map((warning) => (
                      <li key={warning} className="flex items-start gap-3">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span className="leading-6">{warning}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              {hasResults ? (
                <div className="border-primary/20 bg-card mt-8 rounded-[8px] border p-6 shadow-sm sm:p-8">
                  <div className="grid items-center gap-6 lg:grid-cols-[1fr_auto]">
                    <div>
                      <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
                        Go beyond a one-off calculation
                      </p>
                      <h3 className="font-heading text-foreground mt-2 text-2xl font-bold">
                        A reorder date is only useful if something watches it
                      </h3>
                      <p className="text-muted-foreground mt-3 max-w-3xl text-sm leading-7">
                        {siteName} tracks stock, suppliers, and purchase orders
                        together, so the reorder point you just calculated
                        becomes an alert your team acts on instead of a number
                        in a spreadsheet nobody reopens.
                      </p>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                      <Button asChild className="h-11 rounded-xl font-bold">
                        <Link href={startFreePath}>
                          Start Your 14-Day Trial
                        </Link>
                      </Button>
                      <Button
                        asChild
                        variant="outline"
                        className="h-11 rounded-xl font-bold"
                      >
                        <Link href="/#features">See How It Works</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto mb-16 max-w-3xl text-center">
              <h2 className="font-heading text-foreground text-3xl font-bold sm:text-4xl">
                How Lead Time Calculation Works
              </h2>
              <p className="text-muted-foreground mt-4 text-base leading-7">
                Reorder timing improves when every stage between PO placement
                and warehouse receipt is modeled explicitly. Once the order date
                is clear, turn the plan into a supplier-ready{' '}
                <Link
                  href="/free-purchase-order-creator"
                  className="text-primary font-semibold hover:underline"
                >
                  purchase order
                </Link>
                .
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {planningSteps.map((step) => {
                const Icon = step.icon;

                return (
                  <div
                    key={step.title}
                    className="border-border bg-card rounded-[8px] border p-8 shadow-sm"
                  >
                    <div className="bg-primary/10 mb-6 flex h-12 w-12 items-center justify-center rounded-xl">
                      <Icon className="text-primary h-5 w-5" />
                    </div>
                    <h3 className="text-foreground text-xl font-bold">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground mt-3 text-sm leading-7">
                      {step.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="border-border bg-card border-y py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="font-heading text-foreground text-3xl font-bold sm:text-4xl">
                The Formulas This Calculator Uses
              </h2>
              <p className="text-muted-foreground mt-4 text-base leading-7">
                Nothing here is hidden. These are the exact calculations behind
                every number above.
              </p>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-2">
              <div className="border-border bg-muted rounded-2xl border p-6">
                <h3 className="text-foreground text-lg font-bold">
                  Total lead time
                </h3>
                <p className="text-primary mt-2 font-mono text-sm font-semibold">
                  production + transit + customs
                </p>
                <p className="text-muted-foreground mt-2 text-sm leading-7">
                  The safety buffer is deliberately excluded. Lead time is what
                  your supply chain takes; the buffer is a cushion you choose.
                </p>
              </div>

              <div className="border-border bg-muted rounded-2xl border p-6">
                <h3 className="text-foreground text-lg font-bold">
                  Reorder point
                </h3>
                <p className="text-primary mt-2 font-mono text-sm font-semibold">
                  (daily demand × lead time) + safety stock
                </p>
                <p className="text-muted-foreground mt-2 text-sm leading-7">
                  The standard{' '}
                  <Link
                    href="/blog/reorder-point-formula-for-importers"
                    className="text-primary font-semibold hover:underline"
                  >
                    reorder point formula for importers
                  </Link>
                  : cover demand across the lead time, then hold the buffer on
                  top. Use the{' '}
                  <Link
                    href="/tools/moq-calculator"
                    className="text-primary font-semibold hover:underline"
                  >
                    MOQ calculator
                  </Link>{' '}
                  to compare that timing with the quantity your supplier
                  requires.
                </p>
              </div>

              <div className="border-border bg-muted rounded-2xl border p-6">
                <h3 className="text-foreground text-lg font-bold">
                  Safety stock
                </h3>
                <p className="text-primary mt-2 font-mono text-sm font-semibold">
                  daily demand × safety buffer days
                </p>
                <p className="text-muted-foreground mt-2 text-sm leading-7">
                  A days-of-cover model. It is simpler than a service-level
                  formula and does not need demand variability data you probably
                  do not have yet. The longer method is in{' '}
                  <Link
                    href="/blog/how-to-calculate-safety-stock"
                    className="text-primary font-semibold hover:underline"
                  >
                    how to calculate safety stock
                  </Link>
                  .
                </p>
              </div>

              <div className="border-border bg-muted rounded-2xl border p-6">
                <h3 className="text-foreground text-lg font-bold">
                  Latest order date
                </h3>
                <p className="text-primary mt-2 font-mono text-sm font-semibold">
                  today + (days of cover − lead time − buffer)
                </p>
                <p className="text-muted-foreground mt-2 text-sm leading-7">
                  If that date has already passed, the page says how many days
                  late you are. Pair it with the{' '}
                  <Link
                    className="text-primary font-semibold hover:underline"
                    href="/tools/freight-cost-estimator"
                  >
                    sea vs air freight calculator
                  </Link>{' '}
                  when you need to buy those days back.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="border-border relative overflow-hidden rounded-[10px] border bg-[radial-gradient(circle_at_center,var(--primary)_0%,var(--primary)_34%,var(--primary)_100%)] p-10 shadow-[0_20px_50px_-28px_rgba(22,22,42,0.35)]">
                <div className="absolute inset-0 [background-image:linear-gradient(rgba(255,255,255,0.16)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.16)_1px,transparent_1px)] [background-size:18px_18px] opacity-20" />
                <div className="relative flex aspect-[4/3] items-center justify-center">
                  <div className="absolute h-72 w-72 rounded-full border border-white/10" />
                  <div className="absolute h-56 w-56 rounded-full border border-white/15" />
                  <div className="absolute h-40 w-40 rounded-full border border-white/20" />
                  <div className="bg-card/10 flex h-28 w-28 items-center justify-center rounded-full border border-white/20 shadow-2xl backdrop-blur-sm">
                    <Network className="text-primary-foreground h-12 w-12" />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-foreground text-3xl font-bold sm:text-4xl">
                  Why Lead Time Planning Matters
                </h2>

                <div className="mt-8 space-y-6">
                  {whyItMatters.map((item) => (
                    <div key={item.title} className="flex gap-4">
                      <div className="bg-sage-fg/10 text-sage-fg mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                        <CheckCircle className="h-4 w-4" />
                      </div>
                      <div>
                        <h3 className="text-foreground text-lg font-bold">
                          {item.title}
                        </h3>
                        <p className="text-muted-foreground mt-1 text-sm leading-7">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="border-border bg-card rounded-2xl border p-5">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-xl">
                        <Bell className="text-primary h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm font-semibold">
                          Reorder alerts
                        </p>
                        <p className="text-foreground text-lg font-bold">
                          Surface timing risk earlier
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-border bg-card rounded-2xl border p-5">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-xl">
                        <Calendar className="text-primary h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm font-semibold">
                          Planning rhythm
                        </p>
                        <p className="text-foreground text-lg font-bold">
                          See order deadlines clearly
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-border bg-card rounded-2xl border p-5 sm:col-span-2">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-xl">
                        <TrendingUp className="text-primary h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm font-semibold">
                          Operational accuracy
                        </p>
                        <p className="text-foreground text-lg font-bold">
                          Track the difference between planned and real lead
                          time
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-card py-24">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-foreground text-center text-3xl font-bold sm:text-4xl">
              Lead Time Calculator FAQ
            </h2>

            <Accordion type="single" collapsible className="mt-12 space-y-4">
              {faqItems.map((faq) => (
                <AccordionItem
                  key={faq.value}
                  value={faq.value}
                  className="border-border rounded-[8px] border px-6"
                >
                  <AccordionTrigger className="text-foreground text-left text-base font-bold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6 text-sm leading-7">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-16">
              <h2 className="text-foreground text-2xl font-bold">
                Related reading
              </h2>
              <ul className="text-muted-foreground mt-4 space-y-3 text-sm leading-7">
                <li>
                  <Link
                    href="/blog/reorder-point-formula-for-importers"
                    className="text-primary font-semibold hover:underline"
                  >
                    Reorder point formula for importers
                  </Link>{' '}
                  — why quote lead time is the wrong input when production
                  slips.
                </li>
                <li>
                  <Link
                    href="/blog/how-to-calculate-safety-stock"
                    className="text-primary font-semibold hover:underline"
                  >
                    How to calculate safety stock
                  </Link>{' '}
                  — when a days-of-cover buffer is not enough.
                </li>
              </ul>
            </div>

            <RelatedTools currentPath="/tools/lead-time-calculator" />
          </div>
        </section>

        <section className="bg-foreground text-background relative overflow-hidden py-24 sm:py-32">
          <div className="bg-primary/20 absolute -top-48 -right-32 h-[520px] w-[520px] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-white/5 blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div className="max-w-xl">
              <p className="text-primary text-xs font-bold tracking-[0.24em] uppercase">
                The calculator is free. Missing the date is not.
              </p>
              <h2 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Stockouts are not a math problem. They are a memory problem.
              </h2>
              <p className="text-background/70 mt-6 text-lg leading-8">
                You just worked out exactly when to reorder. The hard part is
                remembering on that day, for every SKU, while suppliers slip and
                demand moves. {siteName} keeps stock levels, supplier lead
                times, and purchase orders in one workspace and tells you when
                the window is closing.
              </p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  className="bg-background text-foreground hover:bg-background/90 h-12 rounded-xl px-7 font-bold"
                >
                  <Link href={startFreePath}>Start Your 14-Day Trial</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="text-background hover:bg-background/10 h-12 rounded-xl border-white/25 bg-transparent px-7 font-bold"
                >
                  <Link href="/#features">See {siteName}</Link>
                </Button>
              </div>

              <div className="text-background/60 mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                {['No credit card', 'Live in 15 minutes', 'Cancel anytime'].map(
                  (item) => (
                    <span key={item} className="flex items-center gap-2">
                      <CheckCircle className="text-primary h-4 w-4" />
                      {item}
                    </span>
                  ),
                )}
              </div>
            </div>

            <figure className="bg-background text-foreground rounded-[32px] p-3 shadow-[0_32px_90px_-32px_rgba(0,0,0,0.7)] sm:p-4">
              <figcaption className="text-muted-foreground px-3 pb-3 text-xs font-bold tracking-[0.16em] uppercase">
                Illustrative {siteName} workspace
              </figcaption>
              <div className="border-border bg-card overflow-hidden rounded-[8px] border">
                <div className="border-border flex items-center justify-between border-b px-5 py-4 sm:px-7">
                  <div className="flex items-center gap-3">
                    <div className="bg-foreground text-background flex h-9 w-9 items-center justify-center rounded-xl">
                      <Network className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-extrabold">{siteName}</p>
                      <p className="text-muted-foreground text-xs">
                        Example replenishment view
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 dark:text-amber-400">
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    Reorder window
                  </div>
                </div>

                <div className="p-5 sm:p-7">
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-muted-foreground text-xs font-bold tracking-[0.18em] uppercase">
                        SKU CB-1180 · 56-day lead time
                      </p>
                      <p className="font-heading mt-2 text-3xl font-black sm:text-4xl">
                        5 days
                      </p>
                      <p className="text-muted-foreground mt-1 text-sm">
                        Until the order must be placed
                      </p>
                    </div>
                    <div className="rounded-full bg-amber-100 px-4 py-2 text-sm font-bold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                      1,500 on hand
                    </div>
                  </div>

                  <div className="mt-8 h-2 overflow-hidden rounded-full bg-black/5 dark:bg-white/10">
                    <div className="bg-primary h-full w-[93%] rounded-full" />
                  </div>
                  <div className="text-muted-foreground mt-2 flex justify-between text-xs">
                    <span>Reorder point 1,400</span>
                    <span>Stockout in 75 days</span>
                  </div>

                  <div className="mt-8 grid gap-3 sm:grid-cols-3">
                    <div className="bg-muted rounded-2xl p-4">
                      <Clock className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">56 days</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Actual supplier lead time
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <Package className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">280</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Safety stock held
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <Calendar className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">Aug 11</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Latest safe order date
                      </p>
                    </div>
                  </div>

                  <div className="border-border mt-6 flex items-center gap-3 border-t pt-5">
                    <CheckCircle className="text-primary h-5 w-5 shrink-0" />
                    <p className="text-sm font-semibold">
                      Alerts fire before the window closes, not after the
                      stockout.
                    </p>
                  </div>
                </div>
              </div>
            </figure>
          </div>
        </section>
      </div>
    </>
  );
}
